// ABOUTME: SPEC-53 — authored geometry of the static collection carousel (Figma 403:656, 3840x2160)
// ABOUTME: and the pure maths that lands it, `object-fit: cover`, on any viewport.

/** The composite image and every position below are authored in this frame (the 16:9 desktop crop).
 * Two more real crops of the same background photo — `carousel-wide.jpg` (~2.25:1, Figma 403:1046's
 * source, includes the scene's flanking statues) and `carousel-tall.jpg` (~4:3) — cover the rest of
 * the desktop window-shape range; `DESKTOP_VARIANTS` and `pickDesktopVariant` below choose whichever
 * of the three is closest to the window's own aspect, so `coverBox`'s crop on top of that (client
 * override 2026-09-14: always fill the screen, crop rather than letterbox) is normally small. Mobile
 * swaps to a different composition entirely (the grid layout, Figma 452:4043, see `GRID_DESIGN`),
 * gated on device via `useBreakpoint()` in `CollectionStage.js`. */
export const DESIGN = { width: 3840, height: 2160 };

/**
 * The five watch layers, Figma 403:656 — each ~727x938 at y=611, x left→right: 209 (`image 397`),
 * 883 (`image 402`), 1556 (`image 400`), 2229 (`image 398`), 2903 (`image 401`). These are the tap
 * zones; the labels sit under them. Nothing here names a piece: the PAYLOAD's slot order decides
 * which watch is which.
 */
export const WATCH = { y: 611, w: 727, h: 938 };
export const SLOT_X = [209, 883, 1556, 2229, 2903];

/**
 * Two more desktop crops of the same background photo, derived (not separately Figma-authored) by
 * cropping+scaling the source at the same 1.8356x factor as the 16:9 crop. `wide` keeps the watches'
 * authored y/h (only the width, and so the x spacing, changes); `tall` keeps the width/x spacing
 * (SLOT_X, unchanged) and shifts everything down by the same amount the crop reveals above it.
 * `label`/`header` y-values shift by the identical delta so the whole composition moves as one.
 */
const WIDE = { width: 4864, height: 2160 };
const WIDE_GAP = (WIDE.width - 5 * WATCH.w) / 6;
export const SLOT_X_WIDE = Array.from({ length: 5 }, (_, i) => WIDE_GAP + i * (WATCH.w + WIDE_GAP));

const TALL = { width: 3840, height: 2880 };
/** The crop's revealed-above-the-old-top delta, at the shared ~1.8356x source scale (stageGeometry
 * derivation notes, not reproduced here — see the Figma background layer 403:1046). Everything
 * authored for the 16:9 frame shifts down by this amount in the taller frame; nothing rescales. */
const TALL_SHIFT = 163.37;

/** Which of the three desktop crops best fits a viewport, by aspect (geometric-mean boundaries). */
export function pickDesktopVariant(viewportW, viewportH) {
  if (!viewportW || !viewportH) return 'default';
  const aspect = viewportW / viewportH;
  const tallAspect = TALL.width / TALL.height; // 1.3333
  const defaultAspect = DESIGN.width / DESIGN.height; // 1.7778
  const wideAspect = WIDE.width / WIDE.height; // 2.2519
  const loBoundary = Math.sqrt(tallAspect * defaultAspect);
  const hiBoundary = Math.sqrt(defaultAspect * wideAspect);
  if (aspect < loBoundary) return 'tall';
  if (aspect >= hiBoundary) return 'wide';
  return 'default';
}

/** Resolve the sibling asset filename for a desktop or mobile variant. Same folder as `basePath`. */
export function variantImagePath(basePath, variant) {
  const src = String(basePath || '').trim();
  if (!src) return '';
  const slash = src.lastIndexOf('/');
  const dir = slash >= 0 ? src.slice(0, slash + 1) : '';
  if (variant === 'mobile-grid') return `${dir}grid.jpg`;
  if (variant === 'mobile-grid-tall') return `${dir}grid-tall.jpg`;
  if (variant === 'mobile-grid-short') return `${dir}grid-short.jpg`;
  if (variant === 'wide' || variant === 'tall') {
    const dot = src.lastIndexOf('.');
    return dot > slash ? `${src.slice(0, dot)}-${variant}${src.slice(dot)}` : `${src}-${variant}`;
  }
  return src;
}

/** Label frames `403:820/823/829/832/838` share one top edge. */
export const LABEL_Y = 1520;
/** Header frame `403:817` ("OPH 786 Vélos" + price in the mock): same box as the reel pill `369:62`. */
export const HEADER = { x: 1533.33, y: 381.33, w: 772.67, h: 174.67 };

/** Smallest subline the labels may render at — the reel rail's own floor (CollectionOverlay). */
export const MIN_SUBLINE_PX = 11;
/** WCAG target floor for the tap zones (portrait phones: ~74px wide at 390, so height is the risk). */
export const MIN_TAP_PX = 44;

/**
 * The full geometry for each desktop crop, keyed the same as `pickDesktopVariant`'s return value and
 * `variantImagePath`'s `variant` argument. `default` is exactly the module-level constants above
 * (unchanged, so existing callers passing no config keep the original 16:9 behaviour).
 */
export const DESKTOP_VARIANTS = {
  default: { design: DESIGN, watch: WATCH, slotX: SLOT_X, labelY: LABEL_Y, header: HEADER },
  wide: { design: WIDE, watch: WATCH, slotX: SLOT_X_WIDE, labelY: LABEL_Y, header: HEADER },
  tall: {
    design: TALL,
    watch: { ...WATCH, y: WATCH.y + TALL_SHIFT },
    slotX: SLOT_X,
    labelY: LABEL_Y + TALL_SHIFT,
    header: { ...HEADER, y: HEADER.y + TALL_SHIFT },
  },
};

/**
 * The mobile grid layout, Figma 452:4043 — five watches in a staggered pentagon (2 top / 1 centre /
 * 2 bottom), an entirely different composition from the desktop row, not a crop of it. Own
 * background, own frame size, own per-slot geometry — slot order matches the payload's slot order
 * exactly as the desktop row does (index 2, `image 400`, sits at top-centre).
 *
 * Same three-crop treatment as desktop (client request 2026-09-14: "mobile should get the same
 * treatment"), all derived from `grid.jpg`, never re-authored in Figma:
 * - `short` (`grid-short.jpg`, ~0.65 aspect) is `grid.jpg` cropped at the bottom, in the existing
 *   empty sky below the watches — nothing shifts, `GRID_SLOTS`/`GRID_HEADER` apply unchanged.
 * - `tall` (`grid-tall.jpg`, ~0.462 aspect, iPhone 14's own ratio) needed new pixels the crop didn't
 *   have: extended upward by all the source headroom available (119px, the same tight top margin the
 *   desktop `tall` crop hit) and further downward into more of the same sky. Everything shifts down
 *   by that 119px top delta — same "shift the whole composition by what the crop revealed above it"
 *   move as desktop `tall`'s `TALL_SHIFT`.
 */
export const GRID_DESIGN = { width: 1216, height: 2160 };
export const GRID_HEADER = { x: 357.668, y: 258.666, w: 500.667, h: 174.667 };
export const GRID_SLOTS = [
  { x: 0, y: 355.332, w: 443, h: 572, labelY: 909.332 },
  { x: 774, y: 355.332, w: 442, h: 571, labelY: 909.332 },
  { x: 386.499, y: 742.001, w: 443, h: 572, labelY: 1296.001 },
  { x: 0, y: 1157, w: 443, h: 572, labelY: 1711 },
  { x: 773, y: 1157, w: 443, h: 572, labelY: 1711 },
];

const GRID_SHORT_DESIGN = { width: 1216, height: 1871 };
const GRID_TALL_DESIGN = { width: 1216, height: 2632 };
const GRID_TALL_SHIFT = 119.0; // the crop's revealed-above-the-old-top delta, mirroring TALL_SHIFT

const shiftGridSlots = (dy) => GRID_SLOTS.map((s) => ({ ...s, y: s.y + dy, labelY: s.labelY + dy }));

/**
 * The full geometry for each mobile crop, keyed the same as `pickMobileVariant`'s return value and
 * `variantImagePath`'s `variant` argument (`mobile-grid` / `mobile-grid-short` / `mobile-grid-tall`).
 * `default` is exactly the module-level constants above (unchanged, so existing callers keep the
 * original behaviour).
 */
export const GRID_VARIANTS = {
  default: { design: GRID_DESIGN, slots: GRID_SLOTS, header: GRID_HEADER },
  short: { design: GRID_SHORT_DESIGN, slots: GRID_SLOTS, header: GRID_HEADER },
  tall: {
    design: GRID_TALL_DESIGN,
    slots: shiftGridSlots(GRID_TALL_SHIFT),
    header: { ...GRID_HEADER, y: GRID_HEADER.y + GRID_TALL_SHIFT },
  },
};

/** Which of the three mobile crops best fits a viewport, by aspect (geometric-mean boundaries) — the
 * mobile analogue of `pickDesktopVariant`. Returns a `GRID_VARIANTS` key. */
export function pickMobileVariant(viewportW, viewportH) {
  if (!viewportW || !viewportH) return 'default';
  const aspect = viewportW / viewportH;
  const tallAspect = GRID_TALL_DESIGN.width / GRID_TALL_DESIGN.height;
  const defaultAspect = GRID_DESIGN.width / GRID_DESIGN.height;
  const shortAspect = GRID_SHORT_DESIGN.width / GRID_SHORT_DESIGN.height;
  const loBoundary = Math.sqrt(tallAspect * defaultAspect);
  const hiBoundary = Math.sqrt(defaultAspect * shortAspect);
  if (aspect < loBoundary) return 'tall';
  if (aspect >= hiBoundary) return 'short';
  return 'default';
}

/** The `variantImagePath` key for a `pickMobileVariant` result. */
export function mobileImageVariant(mobileVariantKey) {
  if (mobileVariantKey === 'tall') return 'mobile-grid-tall';
  if (mobileVariantKey === 'short') return 'mobile-grid-short';
  return 'mobile-grid';
}

/**
 * One tap zone + label anchor per grid slot, as fractions of the given design — the 2D analogue of
 * `slotFractions`. Unlike the row, positions never spread generically: the pentagon is a fixed
 * five-slot design, so a collection with a different slot count still gets this layout (extra slots
 * beyond 5 are dropped rather than invented positions with no authored home).
 *
 * @param {{ design: object, slots: object[] }} [variant] defaults to the base `GRID_DESIGN`/`GRID_SLOTS`,
 *   so existing no-argument callers are unaffected.
 * @returns {{ left: number, top: number, width: number, height: number, centerX: number, labelTop: number }[]}
 */
export function gridSlotFractions(variant = { design: GRID_DESIGN, slots: GRID_SLOTS }) {
  const { design, slots } = variant;
  return slots.map((s) => ({
    left: s.x / design.width,
    top: s.y / design.height,
    width: s.w / design.width,
    height: s.h / design.height,
    centerX: (s.x + s.w / 2) / design.width,
    labelTop: s.labelY / design.height,
  }));
}

/**
 * Where the composite lands under `object-fit: contain`: centred, letterboxed on any ratio that is
 * not 16:9. `scale` is "one authored pixel, in rendered pixels" — the stage's own `--u`.
 *
 * @param {number} viewportW @param {number} viewportH
 * @returns {{ left: number, top: number, width: number, height: number, scale: number }}
 */
export function containBox(viewportW, viewportH, design = DESIGN) {
  if (!viewportW || !viewportH) return { left: 0, top: 0, width: 0, height: 0, scale: 0 };
  const scale = Math.min(viewportW / design.width, viewportH / design.height);
  const width = design.width * scale;
  const height = design.height * scale;
  return { left: (viewportW - width) / 2, top: (viewportH - height) / 2, width, height, scale };
}

/**
 * Where the composite lands under `object-fit: cover`: fills the viewport with zero gap on any
 * ratio, cropping whichever axis overruns (client override 2026-09-14 — a filled screen matters more
 * than never cropping a watch; `pickDesktopVariant` already picks the closest-aspect crop first, so
 * the crop this adds on top is normally small). Same shape as `containBox` — `left`/`top` go negative
 * when an axis overruns, which is exactly right for an absolutely-positioned child of an
 * `overflow: hidden` parent — so `slotFractions`/label positions (percentages of this box) need no
 * change to work under either.
 */
export function coverBox(viewportW, viewportH, design = DESIGN) {
  if (!viewportW || !viewportH) return { left: 0, top: 0, width: 0, height: 0, scale: 0 };
  const scale = Math.max(viewportW / design.width, viewportH / design.height);
  const width = design.width * scale;
  const height = design.height * scale;
  return { left: (viewportW - width) / 2, top: (viewportH - height) / 2, width, height, scale };
}

/** The bounding box of every watch position given, in design-frame pixels. */
function watchBounds(watchBoxes) {
  return {
    left: Math.min(...watchBoxes.map((b) => b.x)),
    top: Math.min(...watchBoxes.map((b) => b.y)),
    right: Math.max(...watchBoxes.map((b) => b.x + b.w)),
    bottom: Math.max(...watchBoxes.map((b) => b.y + b.h)),
  };
}

/**
 * `coverBox`, capped so cropping never eats into a watch (client override 2026-09-14: cover was too
 * aggressive on narrow/tall desktop windows, pushing 2 of 5 watches off-screen entirely). Each axis
 * clamps independently to whichever is smaller — the plain cover scale, or the largest scale that
 * still keeps every watch's bounding box on screen. Below the threshold this is identical to
 * `coverBox` (crop only, uniform scale). Past it, clamping only ONE axis while the other keeps
 * scaling for cover is what guarantees zero empty space even here: the axes end up at different
 * scales, i.e. a proportional stretch instead of a crop — the client's explicit trade (fill the
 * screen and keep every watch visible, over never distorting one) for the extreme case.
 *
 * @param {{x:number,y:number,w:number,h:number}[]} watchBoxes every watch's box, design-frame pixels
 *   (the row's `slotX.map(x => ({x, y: watch.y, w: watch.w, h: watch.h}))`, or `GRID_SLOTS` as-is).
 * @returns {{ left: number, top: number, width: number, height: number, scale: number }}
 *   `scale` is `Math.min(scaleX, scaleY)` — the smaller of the two, for anything (label type, tap-zone
 *   floors) that still wants one number rather than tracking the axes separately.
 */
export function fillBox(viewportW, viewportH, design, watchBoxes) {
  if (!viewportW || !viewportH) return { left: 0, top: 0, width: 0, height: 0, scale: 0 };
  const bounds = watchBounds(watchBoxes);
  const marginX = Math.min(bounds.left, design.width - bounds.right);
  const marginY = Math.min(bounds.top, design.height - bounds.bottom);
  const watchSpanX = design.width - 2 * marginX;
  const watchSpanY = design.height - 2 * marginY;
  const coverScale = Math.max(viewportW / design.width, viewportH / design.height);
  const scaleX = Math.min(coverScale, viewportW / watchSpanX);
  const scaleY = Math.min(coverScale, viewportH / watchSpanY);
  const width = design.width * scaleX;
  const height = design.height * scaleY;
  return { left: (viewportW - width) / 2, top: (viewportH - height) / 2, width, height, scale: Math.min(scaleX, scaleY) };
}

/**
 * One tap zone per slot, as FRACTIONS of the design frame (0..1), so they can be laid out as
 * percentages of the contain box and never re-measured. Five slots use the authored x-positions;
 * any other count spreads the same 727-wide zones evenly (a future collection of four or six).
 *
 * The authored 727-wide layers OVERLAP their neighbours by ~53px (209+727 = 936 > 883): the
 * layers carry transparent margin around each watch. Two overlapping buttons would hand that band
 * to whichever renders later, so each zone is trimmed to the midpoint between its centre and the
 * next — every pixel of the row belongs to exactly one watch. `centerX` stays the layer's centre.
 *
 * @param {number} count
 * @param {{ design: object, slotX: number[], watch: object }} [variant] defaults to the 16:9 desktop
 *   constants above, so existing single-argument callers are unaffected.
 * @returns {{ left: number, top: number, width: number, height: number, centerX: number }[]}
 */
export function slotFractions(count, variant = { design: DESIGN, slotX: SLOT_X, watch: WATCH }) {
  const { design, slotX, watch } = variant;
  const n = Math.max(0, count | 0);
  if (n === 0) return [];
  let xs;
  if (n === slotX.length) {
    xs = slotX;
  } else {
    const gap = (design.width - n * watch.w) / (n + 1);
    xs = Array.from({ length: n }, (_, i) => gap + i * (watch.w + gap));
  }
  const centers = xs.map((x) => x + watch.w / 2);
  return xs.map((x, i) => {
    const left = i === 0 ? x : Math.max(x, (centers[i - 1] + centers[i]) / 2);
    const right = i === n - 1 ? x + watch.w : Math.min(x + watch.w, (centers[i] + centers[i + 1]) / 2);
    return {
      left: left / design.width,
      top: watch.y / design.height,
      width: (right - left) / design.width,
      height: watch.h / design.height,
      centerX: centers[i] / design.width,
    };
  });
}

/**
 * How the labels and header are placed for a given contain box.
 *
 * `inline` — the labels sit at their authored positions inside the frame, at authored size × scale
 * (desktop, and anything wide enough for a 29-authored-px subline to clear the legibility floor).
 * Otherwise (portrait phones, portrait tablets, the 1216x2160 mobile frame) the type would be 4-9px
 * and the five labels would collide, so they REFLOW into one centred row at floor-legible sizes: in
 * the letterbox band under the picture when there is one, else across the picture's own bottom band
 * (the composite's lower 28% is background below the watches).
 *
 * @param {{ top: number, height: number, scale: number }} box from `containBox`
 * @param {number} viewportH
 * @param {number} [subline]
 * @param {{ labelY: number, design: object }} [variant] defaults to the 16:9 desktop constants, so
 *   existing three-argument callers are unaffected.
 * @returns {{ inline: boolean, sizeScale: number, rowTop: number, banded: boolean }}
 *   `sizeScale` multiplies every authored label measurement (type, padding, gap).
 */
export function labelLayout(box, viewportH, subline = 29, variant = { labelY: LABEL_Y, design: DESIGN }) {
  const inline = subline * box.scale >= MIN_SUBLINE_PX;
  const sizeScale = inline ? box.scale : MIN_SUBLINE_PX / subline;
  const bandBelow = viewportH - (box.top + box.height);
  const banded = bandBelow >= MIN_TAP_PX * 1.5;
  const rowTop = banded
    ? box.top + box.height + bandBelow * 0.12
    : box.top + box.height * (variant.labelY / variant.design.height);
  return { inline, sizeScale, rowTop, banded };
}

/** Resolve the payload's image path: URLs, root and relative paths as-is, bare paths from `public/`. */
export function resolveStageImage(image) {
  const src = String(image || '').trim();
  if (!src) return '';
  if (/^(https?:)?\/\//i.test(src) || src.startsWith('/') || src.startsWith('.') || src.startsWith('data:')) return src;
  return `/${src}`;
}
